<script setup lang="ts">
import { computed } from 'vue';

import type { PriceCalendarTile, PriceCalendarTileType } from '../models';
import {
  isWeekDayTile,
  isPreviousMonthDayTile,
  isCurrentMonthPastDayTile,
  isCurrentMonthAvailableDayTile,
  isCurrentMonthUnavailableDayTile,
  isCurrentMonthUnsearchedDayTile,
  isCurrentMonthLoaderDayTile,
  isNextMonthDayTile,
  isCurrentMonthAlternativeProductDayTile,
} from '../models';

import WeekDayTileView from './WeekDayTileView.vue';
import PreviousMonthDayTileView from './PreviousMonthDayTileView.vue';
import CurrentMonthPastDayTileView from './CurrentMonthPastDayTileView.vue';
import CurrentMonthAvailableDayTileView from './CurrentMonthAvailableDayTileView.vue';
import CurrentMonthAlternativeProductDayTileView from './CurrentMonthAlternativeProductDayTileView.vue';
import CurrentMonthUnavailableDayTileView from './CurrentMonthUnavailableDayTileView.vue';
import CurrentMonthUnsearchedDayTileView from './CurrentMonthUnsearchedDayTileView.vue';
import CurrentMonthLoaderDayTileView from './CurrentMonthLoaderDayTileView.vue';
import NextMonthDayTileView from './NextMonthDayTileView.vue';

interface PriceCalendarTileViewProps {
  calendarTile: PriceCalendarTile;
  showPrices: boolean;
  allowSelectionFor: PriceCalendarTileType[];
}

const props = defineProps<PriceCalendarTileViewProps>();

interface PriceCalendarTileViewEmits {
  (eventName: 'select-date', day: Date): void;
}

const emit = defineEmits<PriceCalendarTileViewEmits>();

const allowSelection = computed<boolean>(() =>
  props.allowSelectionFor.includes(props.calendarTile.type)
);
</script>

<template>
  <WeekDayTileView
    v-if="isWeekDayTile(props.calendarTile)"
    :calendar-tile="props.calendarTile"
  />

  <PreviousMonthDayTileView v-if="isPreviousMonthDayTile(props.calendarTile)" />

  <CurrentMonthPastDayTileView
    v-if="isCurrentMonthPastDayTile(props.calendarTile)"
    :calendar-tile="props.calendarTile"
  />

  <CurrentMonthAvailableDayTileView
    v-if="isCurrentMonthAvailableDayTile(props.calendarTile)"
    :calendar-tile="props.calendarTile"
    :show-prices="props.showPrices"
    :allow-selection="allowSelection"
    @select-date="emit('select-date', $event)"
  />

  <CurrentMonthAlternativeProductDayTileView
    v-if="isCurrentMonthAlternativeProductDayTile(props.calendarTile)"
    :calendar-tile="props.calendarTile"
    :show-prices="props.showPrices"
    :allow-selection="allowSelection"
    @select-date="emit('select-date', $event)"
  />

  <CurrentMonthUnavailableDayTileView
    v-if="isCurrentMonthUnavailableDayTile(props.calendarTile)"
    :calendar-tile="props.calendarTile"
    :show-prices="props.showPrices"
    :allow-selection="allowSelection"
    @select-date="emit('select-date', $event)"
  />

  <CurrentMonthUnsearchedDayTileView
    v-if="isCurrentMonthUnsearchedDayTile(props.calendarTile)"
    :calendar-tile="props.calendarTile"
    :show-prices="props.showPrices"
    :allow-selection="allowSelection"
    @select-date="emit('select-date', $event)"
  />

  <CurrentMonthLoaderDayTileView
    v-if="isCurrentMonthLoaderDayTile(props.calendarTile)"
    :show-prices="props.showPrices"
    :calendar-tile="props.calendarTile"
  />

  <NextMonthDayTileView v-if="isNextMonthDayTile(props.calendarTile)" />
</template>
